import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  ShieldCheck, MessageCircle, 
  ChevronRight, Users, Briefcase, 
  Sparkles, FileText
} from 'lucide-react';
import styles from './HelpCenter.module.scss';

const HelpCenter = () => {
  const { t } = useTranslation('common');
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [searchQuery, setSearchQuery] = useState('');

  const helpCategories = [
    {
      icon: <Sparkles size={24} />,
      title: t('help_center.categories.getting_started.title', 'Primeros Pasos'),
      description: t('help_center.categories.getting_started.desc', 'Aprende a configurar tu perfil y a entender el ecosistema FOTH.'),
      links: ['Guía de bienvenida', 'Verificación de cuenta', 'Cómo funciona FOTH']
    },
    {
      icon: <Briefcase size={24} />,
      title: t('help_center.categories.for_brands.title', 'Para Marcas'),
      description: t('help_center.categories.for_brands.desc', 'Todo sobre publicar campañas, gestionar briefs y pagos.'),
      links: ['Creando tu primer brief', 'Suscripciones para marcas', 'Seleccionando creadores']
    },
    {
      icon: <Users size={24} />,
      title: t('help_center.categories.for_creators.title', 'Para Creadores'),
      description: t('help_center.categories.for_creators.desc', 'Consejos para aplicar a campañas y monetizar tu contenido.'),
      links: ['Tips para aplicar con éxito', 'Retiros y Billetera FOTH', 'Entregables y Revision']
    },
    {
      icon: <ShieldCheck size={24} />,
      title: t('help_center.categories.security_payments.title', 'Seguridad y Pagos'),
      description: t('help_center.categories.security_payments.desc', 'Información detallada sobre transacciones y protección de datos.'),
      links: ['Resolución de disputas', 'Suscripciones y acceso', 'Facturación y Recibos']
    }
  ];

  const slideUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.23, 1, 0.32, 1] } }
  };

  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } }
  };

  return (
    <div className={styles.helpPage}>
      {/* 1. HERO / SEARCH */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <motion.div initial="hidden" animate="visible" variants={stagger} className={styles.heroInner}>
            <motion.div variants={slideUp} className={styles.sectionEyebrow}>
              {t('help_center.eyebrow', 'Soporte y Recursos')}
            </motion.div>
            <motion.h1 variants={slideUp} className={styles.heroTitle}>
              {t('help_center.title', 'Centro de Ayuda')}
            </motion.h1>
            
            <motion.div variants={slideUp} className={styles.searchBox}>
              <div className={styles.searchWrapper}>
                <Search className={styles.searchIcon} size={20} />
                <input 
                  type="text" 
                  placeholder={t('help_center.search_placeholder', '¿En qué podemos ayudarte hoy?')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className={styles.searchSuggestions}>
                {t('help_center.suggestions_label', 'Sugerencias:')} <span>Verificación</span>, <span>Suscripciones</span>, <span>Facturación</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 2. CATEGORIES GRID */}
      <section className={styles.categoriesSection}>
        <div className={styles.container}>
          <div className={styles.categoryGrid}>
            {helpCategories.map((cat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={styles.categoryCard}
              >
                <div className={styles.catIcon}>{cat.icon}</div>
                <h3>{cat.title}</h3>
                <p>{cat.description}</p>
                <ul className={styles.linkList}>
                  {cat.links.map((link, j) => (
                    <li key={j}>
                      <Link to="/help">
                        {link} <ChevronRight size={14} />
                      </Link>
                    </li>
                  ))}
                  <li className={styles.allLink}>
                    <Link to="/help">Ver todos los artículos</Link>
                  </li>
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. CTA / CONTACT */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <div className={styles.faqBox}>
            <div className={styles.faqIcon}><MessageCircle size={32} /></div>
            <h2>{t('contact.subtitle', '¿No encuentras lo que buscas?')}</h2>
            <p>{t('contact.subtitle', 'Nuestro equipo de soporte está listo para darte una solución personalizada de forma inmediata.')}</p>
            <Link to="/contact" className={styles.contactBtn}>
              {t('contact.submit', 'Contactar a un experto')}
            </Link>
          </div>
          
          <div className={styles.quickLinks}>
            <div className={styles.quickLinkItem}>
              <FileText size={18} />
              <span>Status: <strong>Operational</strong></span>
            </div>
            <div className={styles.quickLinkItem}>
              <Users size={18} />
              <span>Community</span>
            </div>
            <div className={styles.quickLinkItem}>
              <Briefcase size={18} />
              <span>Partners</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
